import { useState, type CSSProperties } from 'react';
import {
  BAU, FRAGMENTO, PREFIXO_CAPA, TOKEN_CAPA,
  type AltarOferta, type AltarTroca, type Chefe, type ChefeEstado, type ChefeVariante, type EventoAgenda, type EventoTabela
} from '../../api/evento';
import { numero, t } from '../../i18n/idioma';
import { nomeDoItem, nomeDoMapa } from '../../i18n/nomes';
import { corDaMatiz } from '../../look/traje';
import { ItemIcon } from '../ItemIcon';
import { Abas } from '../kit';
import { FaceMob } from '../RetratoMob';
import { Selo } from './Selo';
import { formatar, formatarLongo, restanteDe } from './tempo';
import { TIPO_CHEFE, chefeVivo, proximoDo, quandoDe, quandoTexto } from './tipos';
import { Garantia } from './UltimateBattle';

export type AbaChefe = 'recompensas' | 'altar';

interface Props {
  chefe: Chefe | null;
  agenda: EventoAgenda | null;
  /** A barra do hub: mais nova que o GET, manda na vida e no relógio. */
  barra: ChefeEstado | null;
  /** O 'como' do quadro (aba do UB) cai em Recompensas: o chefe não tem Como funciona — o detalhe fica na Wiki. */
  abaInicial: AbaChefe | 'como';
  busy: boolean;
  onTrocar(oferta: AltarOferta): Promise<AltarTroca | null>;
}

const cor = (matiz: number) => ({ '--chefe': corDaMatiz(matiz) }) as CSSProperties;
const pct = (n: number) => `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
const capaDe = (variante: string) => `${PREFIXO_CAPA}${variante}`;
const ehCapa = (item: string) => item.startsWith(PREFIXO_CAPA);
const aparencia = (v: { matiz: number }) => `balron-${v.matiz}`;

/** O nome de um item do Altar ou do prêmio, inclusive a capa exclusiva e o baú, que não são do catálogo. */
function nomeDe(item: string, reserva?: string): string {
  return nomeDoItem(item, reserva);
}

/**
 * O detalhe do Chefe do Mundo em Mais › Eventos (techspec-chefe-mundo §07): o Balron de agora (a vida do shard, o
 * relógio e o seu lugar no dano), e as abas Recompensas e Altar. O servidor decide tudo; a troca no Altar pede
 * confirmação e mostra a conta. O prêmio e a troca chegam pelo correio (techspec-correio §02).
 */
export function ChefeMundo(p: Props) {
  const { chefe, abaInicial } = p;
  const [aba, setAba] = useState<AbaChefe>(abaInicial === 'como' ? 'recompensas' : abaInicial);
  return (
    <>
      <div className="ev-heroi ev-heroi--compacto">
        <Selo id="chefe" />
        <div>
          <h3>{t('chefe.nome')}</h3>
          <p>{t('chefe.resumo')}</p>
        </div>
      </div>
      <Acao {...p} />
      <Abas
        forma="sublinhada"
        valor={aba}
        onMuda={id => setAba(id as AbaChefe)}
        aria-label={t('chefe.nome')}
        itens={[
          { id: 'recompensas', rotulo: t('evento.aba.recompensas') },
          { id: 'altar', rotulo: t('chefe.aba.altar'), pip: chefe && chefe.altar.saldo > 0 ? chefe.altar.saldo : undefined }
        ]}
      />
      <div className="ev-aba ev-aba--compacta">
        {!chefe && <p className="ev-vazio">{t('chefe.carregando')}</p>}
        {chefe && aba === 'recompensas' && <Recompensas chefe={chefe} />}
        {chefe && aba === 'recompensas' && <p className="ev-dica ev-dica--correio">{t('evento.premio.correio')}</p>}
        {chefe && aba === 'altar' && <Altar chefe={chefe} busy={p.busy} onTrocar={p.onTrocar} />}
      </div>
    </>
  );
}

function Acao({ chefe, agenda, barra }: Props) {
  const ao = chefe?.aoVivo ?? null;
  const vivo = chefeVivo(barra) || (!!ao && ao.estado === 0 && ao.fimEmMs > Date.now());
  if (vivo) {
    const fonte = barra && barra.id === ao?.id ? barra : (ao ?? barra)!;
    const p = Math.max(0, Math.min(100, fonte.permil / 10));
    return (
      <div className="ev-acao chefe-vivo" style={cor(fonte.matiz)}>
        <div className="ev-acao__l">
          <span className="ev-vivo">{t('evento.quadro.aoVivo')}</span>
          <span className="ev-relogio" title={t('chefe.det.foge')}>{formatar(restanteDe(fonte.fimEmMs))}</span>
        </div>
        <div className="chefe-alvo">
          <FaceMob aparencia={aparencia(fonte)} px={46} titulo={fonte.nome} />
          <div>
            <b className="chefe-nome">{fonte.nome}</b>
            <small>{t('chefe.det.onde', { mapa: nomeDoMapa(fonte.mapa), n: numero(fonte.lutadores) })}</small>
          </div>
        </div>
        <span className="chefe-vida" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.ceil(p)}>
          <i style={{ width: `${p}%` }} /><b>{pct(p)}</b>
        </span>
        {ao && (
          <>
            <p className="ev-dica">
              {ao.meuLugar > 0
                ? t('chefe.det.meuLugar', { n: ao.meuLugar, d: numero(ao.meuDano), min: numero(ao.danoMinimo) })
                : t('chefe.det.semDano', { min: numero(ao.danoMinimo), mapa: nomeDoMapa(ao.mapa) })}
            </p>
            {ao.ranking.length > 0 && (
              <ol className="chefe-rank">
                {ao.ranking.slice(0, 5).map(r => (
                  <li key={r.lugar}><b>{r.lugar}º</b><span>{r.nome}</span><span className="ev-mono">{numero(r.dano)}</span><small>{pct(r.pct)}</small></li>
                ))}
              </ol>
            )}
          </>
        )}
      </div>
    );
  }
  const anuncio = chefe?.anunciado ?? null;
  if (anuncio && anuncio.emMs > Date.now()) {
    return (
      <div className="ev-acao" style={cor(anuncio.matiz)}>
        <div className="chefe-alvo">
          <FaceMob aparencia={aparencia(anuncio)} px={46} titulo={anuncio.nome} />
          <div>
            <small>{t('chefe.det.anunciado')}</small>
            <b className="chefe-nome">{anuncio.nome}</b>
            <small>{t('chefe.det.anunciadoOnde', { mapa: nomeDoMapa(anuncio.mapa) })}</small>
          </div>
          <span className="ev-relogio">{formatar(restanteDe(anuncio.emMs))}</span>
        </div>
        <p className="ev-dica">{t('chefe.det.dicaIr', { mapa: nomeDoMapa(anuncio.mapa) })}</p>
      </div>
    );
  }
  const prox = chefe?.proximos.find(x => x.emMs > Date.now()) ?? proximoDo(TIPO_CHEFE, agenda);
  const avisoMin = Math.round((chefe?.regras.avisoSegundos ?? 900) / 60);
  return (
    <div className="ev-acao ev-acao--linha" title={t('chefe.det.dicaAgenda', { a: avisoMin })}>
      <small>{t('evento.det.proximo')}</small>
      {prox ? (
        <b className="ev-acao__quando">
          {quandoTexto(prox, 'frase')}
          {quandoDe(prox) === 'hoje' && <span> · {formatarLongo(restanteDe(prox.emMs))}</span>}
        </b>
      ) : <b className="ev-acao__quando">{t('evento.chip.semHorario')}</b>}
    </div>
  );
}

function Recompensas({ chefe }: { chefe: Chefe }) {
  const mapas = chefe.mapas.filter(m => m.tabela);
  const [mapa, setMapa] = useState<string | null>(null);
  const atual = mapas.find(m => m.mapa === mapa) ?? mapas[0] ?? null;
  if (!atual?.tabela) return <p className="ev-vazio">{t('evento.drop.vazio')}</p>;
  const cores = chefe.variantes.filter(v => v.mapa === atual.mapa);
  return (
    <>
      {mapas.length > 1 && (
        <div className="ev-seg" role="group">
          {mapas.map(m => (
            <button key={m.mapa} type="button" aria-pressed={m.mapa === atual.mapa} onClick={() => setMapa(m.mapa)}>{nomeDoMapa(m.mapa)}</button>
          ))}
        </div>
      )}
      <TabelaChefe tabela={atual.tabela} cores={cores} />
      {atual.tabela.garantiaEventos > 0 && (
        <Garantia n={Math.min(chefe.garantiaContador, atual.tabela.garantiaEventos)} de={atual.tabela.garantiaEventos}
          raridade={atual.tabela.garantiaRaridade ?? 'epico'} />
      )}
      <p className="ev-dica">{t('chefe.recomp.capaRepetida', { n: chefe.regras.capaRepetidaFragmentos })}</p>
    </>
  );
}

/** A tabela do mapa; o item "na cor" vira a capa de cada cor que aparece ali, com a chance dividida. */
function TabelaChefe({ tabela, cores }: { tabela: EventoTabela; cores: readonly ChefeVariante[] }) {
  return (
    <div className="ev-rars ev-rars--compacto">
      {tabela.raridades.map(r => (
        <div key={r.id} className={`ev-rar ev-rar--${r.id}`}>
          <div className="ev-rar__h"><b>{t(`evento.raridade.${r.id}`)}</b><span>{pct(r.chance)}</span></div>
          <div className="ev-itens">
            {r.itens.flatMap(i => {
              const q = i.qtdMin === i.qtdMax ? numero(i.qtdMin) : `${numero(i.qtdMin)}–${numero(i.qtdMax)}`;
              if (i.def === TOKEN_CAPA) {
                const ativas = cores.filter(c => c.ativa);
                return ativas.map(c => {
                  const rot = `${nomeDe(capaDe(c.id))} · ${t('chefe.recomp.naCor')} · ${pct(i.chance)}`;
                  return (
                    <span key={`${r.id}-${c.id}`} className="ev-item ev-item--capa" title={rot}>
                      <ItemIcon id={capaDe(c.id)} size={30} label={rot} />
                    </span>
                  );
                });
              }
              const rot = `${nomeDe(i.def)} ×${q} · ${pct(i.chance)}`;
              return [(
                <span key={`${r.id}-${i.def}`} className="ev-item" title={rot}>
                  <ItemIcon id={i.def} size={30} quantity={i.qtdMax} label={rot} />
                </span>
              )];
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- o Altar

function Altar({ chefe, busy, onTrocar }: { chefe: Chefe; busy: boolean; onTrocar(o: AltarOferta): Promise<AltarTroca | null> }) {
  const [filtro, setFiltro] = useState<string>('tudo');
  const [confirmar, setConfirmar] = useState<AltarOferta | null>(null);
  const [feito, setFeito] = useState<AltarTroca | null>(null);
  const { altar, variantes, regras } = chefe;
  const faixas: { id: string; v: ChefeVariante | null }[] = [
    ...variantes.map(v => ({ id: v.id, v })),
    { id: '', v: null }
  ].filter(f => filtro === 'tudo' || filtro === (f.id || 'geral'));
  const doFiltro = (id: string) => altar.ofertas
    .filter(o => o.variante === id)
    .sort((a, b) => Number(b.destaque) - Number(a.destaque));

  const trocar = async () => {
    if (!confirmar) return;
    const r = await onTrocar(confirmar);
    setConfirmar(null);
    if (r) setFeito(r);
  };

  return (
    <>
      <div className="altar-saldo">
        <ItemIcon id={altar.moeda} size={34} label={nomeDe(altar.moeda)} />
        <span>
          <b className="ev-mono">{numero(altar.saldo)}</b>
          <small>{t('chefe.altar.saldo', { item: nomeDe(altar.moeda) })}</small>
        </span>
      </div>
      <div className="ev-seg altar-filtro" role="group" aria-label={t('chefe.altar.filtro')}>
        <button type="button" aria-pressed={filtro === 'tudo'} onClick={() => setFiltro('tudo')}>{t('chefe.altar.tudo')}</button>
        {variantes.map(v => (
          <button key={v.id} type="button" aria-pressed={filtro === v.id} onClick={() => setFiltro(v.id)} title={v.nome} style={cor(v.matiz)}>
            <i className="altar-bola" aria-hidden="true" />{v.nome.replace(/^Balron\s+(d[aeo]s?\s+|em\s+)?/i, '')}
          </button>
        ))}
        <button type="button" aria-pressed={filtro === 'geral'} onClick={() => setFiltro('geral')}>{t('chefe.altar.geral')}</button>
      </div>
      {feito && (
        <p className="ev-dica altar-feito" role="status">
          {feito.guardaRoupa
            ? t('chefe.altar.feitoCapa', { item: nomeDe(feito.entregue) })
            : feito.raridade && feito.raridade !== 'altar'
              ? t('chefe.altar.feitoBau', { item: nomeDe(feito.entregue), r: t(`evento.raridade.${feito.raridade}`) })
              : t('chefe.altar.feito', { item: nomeDe(feito.entregue), n: feito.qtd })}
        </p>
      )}
      {faixas.map(f => {
        const ofertas = doFiltro(f.id);
        if (ofertas.length === 0) return null;
        return (
          <section key={f.id || 'geral'} className="altar-faixa" style={f.v ? cor(f.v.matiz) : undefined}>
            <header className="altar-faixa__t">
              {f.v ? <FaceMob aparencia={aparencia(f.v)} px={36} titulo={f.v.nome} /> : <Selo id="chefe" />}
              <span>
                <b>{f.v ? f.v.nome : t('chefe.altar.geral')}</b>
                <small>{f.v ? (f.v.ativa ? nomeDoMapa(f.v.mapa) : t('chefe.altar.foraRotacao')) : t('chefe.altar.geralSub')}</small>
              </span>
            </header>
            <div className="altar-ofertas">
              {ofertas.map(o => (
                <Oferta key={o.id} o={o} saldo={altar.saldo} bonus={regras.capaBonus} bau={altar.bau} busy={busy} onTrocar={() => { setFeito(null); setConfirmar(o); }} />
              ))}
            </div>
          </section>
        );
      })}
      <p className="ev-dica">{t('chefe.altar.dica')}</p>
      {confirmar && (
        <div className="altar-confirma" role="dialog" aria-modal="true" aria-labelledby="altar-confirma-t">
          <div className="altar-confirma__c">
            <h4 id="altar-confirma-t">{t('chefe.altar.confirmaT')}</h4>
            <div className="altar-confirma__item">
              <ItemIcon id={confirmar.item} size={56} quantity={confirmar.qtd > 1 ? confirmar.qtd : undefined} label={nomeDe(confirmar.item, confirmar.nome)} eager />
              <b>{nomeDe(confirmar.item, confirmar.nome)}{confirmar.qtd > 1 ? ` ×${numero(confirmar.qtd)}` : ''}</b>
            </div>
            <p className="altar-conta ev-mono">
              {numero(altar.saldo)} − {numero(confirmar.preco)} = <b>{numero(altar.saldo - confirmar.preco)}</b>
            </p>
            <p className="ev-dica">
              {ehCapa(confirmar.item) ? t('chefe.altar.vaiGuardaRoupa', { n: regras.capaBonus })
                : confirmar.item === BAU ? t('chefe.altar.vaiBau') : t('chefe.altar.vaiPremios')}
            </p>
            {confirmar.item === BAU && altar.bau && <ChancesDoBau bau={altar.bau} />}
            <p className="ev-dica ev-dica--aviso">{t('chefe.altar.naoVolta')}</p>
            <div className="row">
              <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => setConfirmar(null)}>{t('comum.cancelar')}</button>
              <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void trocar()} data-foco-inicial>
                {t('chefe.altar.trocar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

type Bau = NonNullable<Chefe['altar']['bau']>;

/** As chances do Baú por raridade, na cor da raridade (o mesmo selo das tabelas de drop). */
function ChancesDoBau({ bau }: { bau: Bau }) {
  return (
    <span className="altar-bau">
      {bau.filter(b => b.chance > 0).map(b => (
        <span key={b.raridade} className={`ev-rar--${b.raridade}`} title={b.tintas.map(x => nomeDe(x)).join(', ')}>
          {t(`evento.raridade.${b.raridade}`)} {pct(b.chance)}
        </span>
      ))}
    </span>
  );
}

function Oferta({ o, saldo, bonus, bau, busy, onTrocar }: {
  o: AltarOferta; saldo: number; bonus: number; bau?: Bau; busy: boolean; onTrocar(): void;
}) {
  const falta = Math.max(0, o.preco - saldo);
  const capa = ehCapa(o.item);
  const tags: string[] = [];
  if (o.limitePeriodo !== 'nenhum') tags.push(t(`chefe.altar.limite.${o.limitePeriodo}`, { n: o.limiteQtd }));
  if (o.restam >= 0) tags.push(t(`chefe.altar.restam.${o.estoquePeriodo}`, { n: o.restam, de: o.estoqueQtd }));
  if (o.ateMs) tags.push(t('chefe.altar.ate', { d: new Date(o.ateMs).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' }) }));
  const bloqueio = o.bloqueio ? t(`chefe.altar.bloqueio.${o.bloqueio}`) : null;
  return (
    <div className={`altar-oferta${o.destaque ? ' is-destaque' : ''}${o.bloqueio ? ' is-off' : ''}`}>
      <ItemIcon id={o.item} size={40} quantity={o.qtd > 1 ? o.qtd : undefined} label={nomeDe(o.item, o.nome)} />
      <span className="altar-oferta__corpo">
        <b>{o.destaque && <span className="altar-estrela" aria-hidden="true">★</span>}{nomeDe(o.item, o.nome)}{o.qtd > 1 ? ` ×${numero(o.qtd)}` : ''}</b>
        {capa && <small className="altar-capa">{t('chefe.altar.capaBonus', { n: bonus })} · {t('chefe.altar.capaUnica')}</small>}
        {o.variante && !capa && <small className="altar-so">{t('chefe.altar.soBalron')}</small>}
        {o.item === BAU && bau && <ChancesDoBau bau={bau} />}
        {tags.length > 0 && <small>{tags.join(' · ')}</small>}
        <span className="altar-preco">
          <ItemIcon id={FRAGMENTO} size={16} label="" />
          <b className="ev-mono">{numero(o.preco)}</b>
          <i className="altar-preco__bar"><i style={{ width: `${Math.min(100, (saldo / Math.max(1, o.preco)) * 100)}%` }} /></i>
        </span>
      </span>
      <button type="button" className="btn btn--mini btn--primary" disabled={busy || !!o.bloqueio || falta > 0} onClick={onTrocar}>
        {bloqueio ?? (falta > 0 ? t('chefe.altar.faltam', { n: numero(falta) }) : t('chefe.altar.trocar'))}
      </button>
    </div>
  );
}
